import { useProfileQuery } from "@/hooks/api/profile.queries";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Edit, Building2, MapPin, Users, Calendar, Briefcase } from "lucide-react";
import { useState } from "react";
import ProfileForm from "./ProfileForm";

const Profile = () => {
  const { data: profile, isLoading, isError } = useProfileQuery();
  const [isEditing, setIsEditing] = useState(false);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 p-6 md:p-8 w-full max-w-7xl mx-auto animate-in fade-in duration-500">
        {/* Banner Skeleton */}
        <Skeleton className="w-full h-48 md:h-64 rounded-2xl" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative -mt-20 px-4">
          <div className="md:col-span-1 space-y-6">
            <Skeleton className="w-32 h-32 rounded-xl shadow-lg" />
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
          <div className="md:col-span-2 space-y-6 mt-16 md:mt-0">
            <Card className="rounded-xl border-border/40 shadow-sm">
              <CardHeader><Skeleton className="h-6 w-1/3" /></CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !profile) {
    return (
      <div className="flex h-full min-h-[500px] items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg border-red-100/50 dark:border-red-900/20 bg-red-50/50 dark:bg-red-900/10">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-2">
              <Building2 className="h-8 w-8 text-red-500" />
            </div>
            <h3 className="text-xl font-semibold text-foreground">Profile Not Found</h3>
            <p className="text-muted-foreground text-sm">We couldn't load the company profile data. Please try again.</p>
            <Button
              className="mt-4"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="p-6 md:p-8 w-full max-w-7xl mx-auto animate-in fade-in zoom-in-95 duration-300">
        <ProfileForm initialData={profile} onCancel={() => setIsEditing(false)} />
      </div>
    );
  }

  // Fallback initial
  const companyInitial = profile.industry ? profile.industry.charAt(0).toUpperCase() : "C";

  return (
    <div className="flex flex-col w-full min-h-full pb-10 bg-slate-50/50 dark:bg-background/95">
      {/* Hero Banner Area */}
      <div className="relative w-full h-56 md:h-80 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 overflow-hidden shadow-inner">
        {/* Abstract background elements */}
        <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 w-96 h-96 bg-white/20 rounded-full blur-3xl animate-pulse mix-blend-overlay" />
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/3 w-72 h-72 bg-white/20 rounded-full blur-2xl animate-pulse mix-blend-overlay" style={{ animationDelay: "1s" }} />

        <div className="absolute top-6 right-6 z-10">
          <Button
            onClick={() => setIsEditing(true)}
            size="sm"
            className="bg-white/20 hover:bg-white/40 text-white border-white/30 backdrop-blur-md shadow-lg transition-all rounded-full px-6"
            variant="outline"
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit Profile
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="px-4 md:px-10 max-w-7xl mx-auto w-full relative z-10">
        <div className="flex flex-col md:flex-row gap-8">

          {/* Left Column (Avatar & Quick Info) */}
          <div className="w-full md:w-1/3 lg:w-1/4 -mt-20 md:-mt-28 space-y-6">
            <Card className="border-white/40 dark:border-border/40 shadow-2xl overflow-hidden backdrop-blur-xl bg-white/80 dark:bg-card/80 rounded-3xl">
              <CardContent className="p-8 flex flex-col items-center text-center space-y-5">
                <div className="relative group">
                  <div className="absolute -inset-2 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-full blur-md opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
                  <Avatar className="relative h-36 w-36 md:h-44 md:w-44 border-4 border-white dark:border-background shadow-2xl ring-4 ring-white/30">
                    <AvatarFallback className="text-6xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 text-white font-black shadow-inner">
                      {companyInitial}
                    </AvatarFallback>
                  </Avatar>
                </div>

                <div className="space-y-2 w-full mt-4">
                  <h2 className="text-3xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300">Organization View</h2>
                  <div className="flex items-center justify-center gap-2 mt-3 text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 py-2 px-4 rounded-full text-sm font-semibold inline-flex mx-auto border border-indigo-100 dark:border-indigo-800/50 shadow-sm transition-transform hover:scale-105">
                    <Briefcase className="h-4 w-4" />
                    <span>{profile.industry || "Industry Not set"}</span>
                  </div>
                </div>

                <Separator className="w-full my-4 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent" />

                <div className="w-full space-y-5 text-left">
                  <div className="flex items-center gap-4 text-sm group">
                    <div className="p-3 bg-gradient-to-br from-pink-100 to-pink-50 dark:from-pink-900/40 dark:to-pink-900/10 rounded-xl text-pink-600 shadow-sm group-hover:scale-110 transition-transform">
                      <Users className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs font-bold uppercase tracking-widest mb-1">Company Size</p>
                      <p className="font-semibold text-foreground text-base">{profile.company_size || "Not set"}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm group">
                    <div className="p-3 bg-gradient-to-br from-purple-100 to-purple-50 dark:from-purple-900/40 dark:to-purple-900/10 rounded-xl text-purple-600 shadow-sm group-hover:scale-110 transition-transform">
                      <MapPin className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs font-bold uppercase tracking-widest mb-1">Headquarters</p>
                      <p className="font-semibold text-foreground text-base">{profile.headquarters || "Not set"}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm group">
                    <div className="p-3 bg-gradient-to-br from-indigo-100 to-indigo-50 dark:from-indigo-900/40 dark:to-indigo-900/10 rounded-xl text-indigo-600 shadow-sm group-hover:scale-110 transition-transform">
                      <Calendar className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs font-bold uppercase tracking-widest mb-1">Founded Year</p>
                      <p className="font-semibold text-foreground text-base">{profile.founded_year || "Not set"}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* System Info Card */}
            <Card className="border-white/40 dark:border-border/40 shadow-lg bg-gradient-to-b from-white/60 to-white/30 dark:from-muted/30 dark:to-muted/10 backdrop-blur-md rounded-2xl">
              <CardContent className="p-6 flex flex-col gap-4">
                <h3 className="text-sm font-bold flex items-center gap-2 text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  <Building2 className="h-4 w-4 text-indigo-500" />
                  System Record
                </h3>
                <div className="space-y-3 text-sm text-muted-foreground font-medium">
                  {profile.created_at && (
                    <div className="flex justify-between items-center bg-white/50 dark:bg-black/20 p-2 rounded-lg">
                      <span>Created On:</span>
                      <span className="text-foreground/90 font-semibold">{new Date(profile.created_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  {profile.updated_at && (
                    <div className="flex justify-between items-center bg-white/50 dark:bg-black/20 p-2 rounded-lg">
                      <span>Last Updated:</span>
                      <span className="text-foreground/90 font-semibold">{new Date(profile.updated_at).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column (Description & Details) */}
          <div className="w-full md:w-2/3 lg:w-3/4 md:mt-10 space-y-8 pb-12">
            <Card className="border-white/40 dark:border-border/40 shadow-xl backdrop-blur-md bg-white/80 dark:bg-card/80 rounded-3xl overflow-hidden">
              <CardHeader className="pb-6 border-b border-gray-100 dark:border-border/30 bg-gray-50/50 dark:bg-muted/10">
                <CardTitle className="text-2xl font-extrabold flex items-center gap-3 text-gray-800 dark:text-gray-100">
                  <div className="h-10 w-2.5 bg-gradient-to-b from-indigo-500 to-pink-500 rounded-full shadow-sm" />
                  About Organization
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                {profile.description ? (
                  <div className="prose prose-base md:prose-lg dark:prose-invert max-w-none">
                    <p className="text-gray-600 dark:text-gray-300 leading-loose whitespace-pre-wrap font-medium tracking-wide">
                      {profile.description}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-16 text-center bg-gradient-to-b from-gray-50 to-white dark:from-muted/20 dark:to-background rounded-2xl dashed-border border-2 border-dashed border-indigo-200 dark:border-indigo-900/50 shadow-inner">
                    <div className="p-5 bg-indigo-100 dark:bg-indigo-900/30 rounded-full mb-6 animate-bounce">
                      <Building2 className="h-10 w-10 text-indigo-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-3">No Description Available</h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-md font-medium leading-relaxed">
                      Share your organization's vision, culture, and achievements to stand out to potential candidates.
                    </p>
                    <Button onClick={() => setIsEditing(true)} size="lg" className="rounded-full shadow-lg hover:shadow-xl transition-all bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-8">
                      <Edit className="mr-2 h-5 w-5" />
                      Add Company Details
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Profile;

